import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Trash2, Check, RotateCcw, Mail, Inbox, CheckCircle2, Clock, BarChart3 } from "lucide-react";
import { isInternal, setInternal } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-[#0c0c0d] border border-white/10 clip-frame p-6" data-testid={`stat-${label}`}>
    <div className="flex items-center justify-between">
      <span className="font-mono-p text-xs uppercase tracking-widest text-white/40">{label}</span>
      <Icon size={18} className="text-primary-c" />
    </div>
    <div className="font-display font-black text-5xl mt-4">{value}</div>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-[#0c0c0d] border border-white/10 clip-frame p-6">
    <span className="font-mono-p text-xs uppercase tracking-widest text-white/40">{label}</span>
    <div className="font-display font-black text-4xl mt-3">{value}</div>
  </div>
);

const Audience = ({ data }) => {
  if (!data) return <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[var(--primary)] rounded-full animate-spin" /></div>;
  const max = Math.max(1, ...data.by_day.map((d) => d.views));
  return (
    <div className="space-y-8 mt-10" data-testid="audience-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <MiniStat label="Vues totales" value={data.total} />
        <MiniStat label="Visiteurs uniques" value={data.unique} />
        <MiniStat label="Vues (7 j)" value={data.views_7d} />
        <MiniStat label="Aujourd'hui" value={data.views_today} />
      </div>
      <div className="bg-[#0c0c0d] border border-white/10 clip-frame p-6">
        <div className="flex items-center justify-between mb-8">
          <h3 className="font-display font-semibold text-lg">Vues — 14 derniers jours</h3>
          <span className="font-mono-p text-xs text-white/40">{data.views_30d} vues / 30 j</span>
        </div>
        <div className="flex items-end gap-1.5 h-40">
          {data.by_day.map((d, i) => (
            <div key={i} className="flex-1 group relative flex flex-col justify-end h-full">
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 font-mono-p text-[10px] text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">{d.views}</span>
              <div className="bg-[var(--primary)]/30 group-hover:bg-[var(--primary)] rounded-t transition-colors" style={{ height: `${(d.views / max) * 100}%`, minHeight: d.views ? "4px" : "0" }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-3 font-mono-p text-[10px] text-white/30">
          <span>{data.by_day[0]?.date.slice(5)}</span>
          <span>{data.by_day[data.by_day.length - 1]?.date.slice(5)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0c0c0d] border border-white/10 clip-frame p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Pages populaires</h3>
          {data.top_pages.length === 0 ? <p className="text-white/40 text-sm">Aucune donnée pour l'instant.</p> : (
            <ul className="space-y-2">{data.top_pages.map((p, i) => (
              <li key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-white/70 font-mono-p truncate">{p.path}</span>
                <span className="text-primary-c font-semibold">{p.views}</span>
              </li>))}</ul>
          )}
        </div>
        <div className="bg-[#0c0c0d] border border-white/10 clip-frame p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Sources de trafic</h3>
          {data.top_referrers.length === 0 ? <p className="text-white/40 text-sm">Trafic direct uniquement pour l'instant.</p> : (
            <ul className="space-y-2">{data.top_referrers.map((r, i) => (
              <li key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-white/70 font-mono-p truncate max-w-[70%]">{r.referrer}</span>
                <span className="text-primary-c font-semibold">{r.views}</span>
              </li>))}</ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, processed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("demandes");
  const [analytics, setAnalytics] = useState(null);
  const [internal, setInternalState] = useState(isInternal());

  const toggleInternal = () => {
    const v = !internal;
    setInternal(v);
    setInternalState(v);
    toast.success(v ? "Cet appareil est exclu des statistiques" : "Cet appareil est de nouveau compté");
  };

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        fetch(`${API}/admin/contacts`, { credentials: "include" }),
        fetch(`${API}/admin/stats`, { credentials: "include" }),
      ]);
      if (!c.ok || !s.ok) throw new Error();
      setItems(await c.json());
      setStats(await s.json());
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadAnalytics = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/analytics`, { credentials: "include" });
      if (!r.ok) throw new Error();
      setAnalytics(await r.json());
    } catch { toast.error("Erreur de chargement des statistiques"); }
  }, []);

  useEffect(() => { if (view === "audience" && !analytics) loadAnalytics(); }, [view, analytics, loadAnalytics]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login");
  };

  const toggleProcessed = async (item) => {
    try {
      const r = await fetch(`${API}/admin/contacts/${item.id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ processed: !item.processed }),
      });
      if (!r.ok) throw new Error();
      toast.success(item.processed ? "Marquée non traitée" : "Marquée traitée");
      load();
    } catch { toast.error("Action impossible"); }
  };

  const remove = async (item) => {
    try {
      const r = await fetch(`${API}/admin/contacts/${item.id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error();
      toast.success("Demande supprimée");
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      load();
    } catch { toast.error("Suppression impossible"); }
  };

  const visible = items.filter((i) =>
    filter === "all" ? true : filter === "pending" ? !i.processed : i.processed
  );

  const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise" />
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[rgba(5,5,6,0.8)] backdrop-blur-xl">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[var(--primary)]" />
            <span className="font-display font-extrabold text-lg tracking-tight">FIXE<span className="text-primary-c">DRONE</span></span>
            <span className="ml-3 font-mono-p text-xs uppercase tracking-widest text-white/40 border-l border-white/15 pl-3">Admin</span>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-[var(--primary)] hover:text-primary-c font-mono-p text-sm transition-colors">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center gap-2 mb-10">
          <button onClick={() => setView("demandes")} data-testid="tab-demandes"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-mono-p text-sm transition-colors ${view === "demandes" ? "bg-[var(--primary)] text-black" : "border border-white/15 text-white/60 hover:text-white"}`}>
            <Inbox size={15} /> Demandes
          </button>
          <button onClick={() => setView("audience")} data-testid="tab-audience"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-mono-p text-sm transition-colors ${view === "audience" ? "bg-[var(--primary)] text-black" : "border border-white/15 text-white/60 hover:text-white"}`}>
            <BarChart3 size={15} /> Audience
          </button>
        </div>

        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-none">{view === "demandes" ? "Demandes de contact" : "Audience du site"}</h1>
        <p className="text-white/50 mt-3">{view === "demandes" ? "Lisez, traitez et gérez les demandes reçues via le formulaire." : "Suivi des visites (statistiques internes anonymes + Google Analytics)."}</p>

        {view === "audience" ? (
          <>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#0c0c0d] border border-white/10 clip-frame p-5">
              <div>
                <p className="font-display font-semibold">Exclure cet appareil des statistiques</p>
                <p className="text-white/45 text-sm mt-1">Vos propres visites (ce navigateur) ne seront comptées ni dans le suivi interne ni dans Google Analytics.</p>
              </div>
              <button onClick={toggleInternal} data-testid="toggle-internal"
                className={`shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-mono-p text-sm transition-colors ${internal ? "bg-[var(--primary)] text-black" : "border border-white/20 text-white/70 hover:text-white"}`}>
                {internal ? <><Check size={15} /> Appareil exclu</> : "Exclure cet appareil"}
              </button>
            </div>
            <Audience data={analytics} />
          </>
        ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          <Stat icon={Inbox} label="Total" value={stats.total} />
          <Stat icon={Clock} label="En attente" value={stats.pending} />
          <Stat icon={CheckCircle2} label="Traitées" value={stats.processed} />
        </div>

        <div className="flex items-center gap-2 mt-12 mb-6">
          {[["all", "Toutes"], ["pending", "En attente"], ["processed", "Traitées"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-4 py-2 rounded-full font-mono-p text-sm transition-colors ${filter === k ? "bg-[var(--primary)] text-black" : "border border-white/15 text-white/60 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[var(--primary)] rounded-full animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="py-20 text-center text-white/40 border border-dashed border-white/10 clip-frame">
            <Mail className="mx-auto mb-4 text-white/30" size={32} />
            Aucune demande {filter !== "all" ? "dans ce filtre" : "pour le moment"}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="contacts-list">
            <AnimatePresence>
              {visible.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  data-testid={`contact-item-${item.id}`}
                  className={`border clip-frame p-6 bg-[#0c0c0d] ${item.processed ? "border-white/5 opacity-70" : "border-white/12"}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display font-semibold text-xl">{item.name}</h3>
                        {item.processed ? (
                          <span className="font-mono-p text-[10px] uppercase tracking-widest px-2 py-1 bg-[var(--primary)]/15 text-primary-c">Traitée</span>
                        ) : (
                          <span className="font-mono-p text-[10px] uppercase tracking-widest px-2 py-1 bg-[#ff5e00]/15 text-[#ff5e00]">En attente</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 font-mono-p text-sm text-white/50">
                        <a href={`mailto:${item.email}`} className="hover:text-primary-c">{item.email}</a>
                        {item.company && <span>· {item.company}</span>}
                        <span>· {fmt(item.created_at)}</span>
                      </div>
                      {item.subject && <p className="mt-3 text-white/80 font-medium">{item.subject}</p>}
                      <p className="mt-2 text-white/60 leading-relaxed whitespace-pre-wrap">{item.message}</p>
                    </div>
                    <div className="flex md:flex-col gap-2 shrink-0">
                      <button onClick={() => toggleProcessed(item)} data-testid={`toggle-${item.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-[var(--primary)] hover:text-primary-c font-mono-p text-xs transition-colors">
                        {item.processed ? <><RotateCcw size={14} /> Rouvrir</> : <><Check size={14} /> Traiter</>}
                      </button>
                      <button onClick={() => remove(item)} data-testid={`delete-${item.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-red-500 hover:text-red-500 font-mono-p text-xs transition-colors">
                        <Trash2 size={14} /> Supprimer
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
